import { Injectable, OnDestroy, signal } from '@angular/core';
import { Client } from '@stomp/stompjs';

import { Subject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { KitchenEvent, OrderStatusEvent, StockAlertEvent } from '../models/websocket.model';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService implements OnDestroy {
  private stompClient: Client | null = null;
  
  // Subjects for events
  kitchenEvents$ = new Subject<KitchenEvent>();
  stockAlerts$ = new Subject<StockAlertEvent>();

  isConnected = signal(false);

  connect(cafeId: string): void {
    if (this.stompClient && this.stompClient.connected) return;

    try {
      const token = localStorage.getItem('ACCESS_TOKEN') || ''; // Access token directly or inject AuthService
      
      const wsUrl = environment.wsUrl.replace(/^http/, 'ws');
      this.stompClient = new Client({
        brokerURL: `${wsUrl}/websocket?token=${token}`,
        debug: (msg) => console.log(`[STOMP] ${msg}`),
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
      });

      this.stompClient.onConnect = (frame) => {
        this.isConnected.set(true);
        console.log('[WebsocketService] Connected to WebSocket');

        // Subscribe to cafe specific topics
        this.stompClient?.subscribe(`/topic/cafe/${cafeId}/kitchen`, (message) => {
          try {
            this.kitchenEvents$.next(JSON.parse(message.body));
          } catch (e) {
            console.error('[WebsocketService] Error parsing kitchen event:', e);
          }
        });

        this.stompClient?.subscribe(`/topic/cafe/${cafeId}/stock-alerts`, (message) => {
          try {
            this.stockAlerts$.next(JSON.parse(message.body));
          } catch (e) {
            console.error('[WebsocketService] Error parsing stock alert:', e);
          }
        });
      };

      this.stompClient.onStompError = (frame) => {
        console.error('[WebsocketService] STOMP error:', frame.headers['message']);
        console.error('[WebsocketService] Details:', frame.body);
      };

      this.stompClient.onDisconnect = () => {
        this.isConnected.set(false);
        console.log('[WebsocketService] Disconnected');
      };

      this.stompClient.activate();
    } catch (error) {
      console.error('[WebsocketService] Failed to initialize WebSocket connection:', error);
    }
  }

  subscribeToOrder(cafeId: string, orderId: string): Observable<OrderStatusEvent> {
    const orderSubject = new Subject<OrderStatusEvent>();
    
    if (this.stompClient && this.stompClient.connected) {
      try {
        this.stompClient.subscribe(`/topic/cafe/${cafeId}/orders/${orderId}`, (message) => {
          orderSubject.next(JSON.parse(message.body));
        });
      } catch (e) {
        console.error('[WebsocketService] Error subscribing to order:', e);
      }
    }

    return orderSubject.asObservable();
  }

  disconnect(): void {
    if (this.stompClient) {
      this.stompClient.deactivate();
      this.isConnected.set(false);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
